<template>
  <time v-bind:datetime="isoFormat">
    <slot name="prepend"></slot>{{ finalValue }}<slot name="append"></slot>
  </time>
</template>

<script>
import Vue from "vue";
import {DatetimeUtil} from "@nordiskemedier/main-components";
import {DateTime} from "luxon";

Vue.use(DatetimeUtil);

export default {
  name: "smart-date",
  props: {
    value: {
      type: Object,
      validator: function (value) {
        return DateTime.isDateTime(value);
      }
    },
    label: {
      type: String,
      default: "{0} at {1}"
    },
    todayLabel: {
      type: String,
      default: "Today"
    },
    yesterdayLabel: {
      type: String,
      default: "Yesterday"
    },
    tomorrowLabel: {
      type: String,
      default: "Tomorrow"
    },
    capitalize: {
      type: Boolean,
      default: false
    },
    /**
     * Supports Luxon tokens (https://moment.github.io/luxon/#/parsing?id=table-of-tokens).
     */
    yearFormat: {
      type: String,
      default: "MMMM d, yyyy"
    },
    /**
     * Supports Luxon tokens (https://moment.github.io/luxon/#/parsing?id=table-of-tokens).
     */
    dateFormat: {
      type: String,
      default: "MMMM d"
    },
    /**
     * Supports Luxon tokens (https://moment.github.io/luxon/#/parsing?id=table-of-tokens).
     */
    timeFormat: {
      type: String,
      default: "HH:mm"
    },
    /**
     * Values range from 1 to 7 for Mon to Sun. Default 1 is Mon.
     */
    firstWeekday: {
      type: Number,
      default: 1,
      validator: function (value) {
        return value >= 1 && value <= 7;
      }
    },
    locale: {
      type: String,
      default: "en"
    },
  },
  computed: {
    finalValue: function () {
      if (this.relativeValue && this.relativeValue.length) {
        return this.relativeValue.substring(0, 1).toUpperCase() + this.relativeValue.substring(1);
      }
      return this.relativeValue;
    },
    relativeValue: function () {
      const now = Vue.$luxon.DateTime.now();
      if (Vue.$datetimeUtil.isSameDate(this.value, now)) {
        return this.label.replace("{0}", this.todayLabel).replace("{1}", this.formattedTimeValue);
      }
      const yesterday = now.minus({days: 1});
      if (Vue.$datetimeUtil.isSameDate(this.value, yesterday)) {
        return this.label.replace("{0}", this.yesterdayLabel).replace("{1}", this.formattedTimeValue);
      }
      const tomorrow = now.plus({days: 1});
      if (Vue.$datetimeUtil.isSameDate(this.value, tomorrow)) {
        return this.label.replace("{0}", this.tomorrowLabel).replace("{1}", this.formattedTimeValue);
      }
      const diff = this.value.diff(now, ["days"]);
      if (diff.days > -200 && diff.days < 200) {
        return this.label.replace("{0}", this.formattedDateValue).replace("{1}", this.formattedTimeValue);
      }
      return this.formattedYearValue;
    },
    formattedYearValue: function () {
      return Vue.$datetimeUtil.getAsString(this.value, this.yearFormat, this.datetimeUtilOptions);
    },
    formattedDateValue: function () {
      return Vue.$datetimeUtil.getAsString(this.value, this.dateFormat, this.datetimeUtilOptions);
    },
    formattedTimeValue: function () {
      return Vue.$datetimeUtil.getAsString(this.value, this.timeFormat, this.datetimeUtilOptions);
    },
    isoFormat: function () {
      return Vue.$datetimeUtil.getAsString(this.value, "yyyy-MM-dd HH:mm:ss", this.datetimeUtilOptions);
    },
    datetimeUtilOptions: function () {
      return {
        firstWeekday: this.firstWeekday,
        locale: this.locale
      }
    }
  }
}
</script>
